"use client";

import { AutocompleteInput } from "@/components/search/autocomplete-input";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import type { AutocompleteResult } from "@/lib/api/types";
import { FilterCheckboxInput } from "@/features/search/components/filters/input/checkbox-input";
import { FilterSelectInput } from "@/features/search/components/filters/input/select-input";
import {
  GS_GRADES,
  MAX_OCCUPATIONAL_SERIES,
  gsGradeLabel,
  normalizeSeriesFreeText,
  seriesCode,
} from "@/features/search/lib/federal";
import {
  ClearChipsButton,
  FilterChip,
  FilterFieldContainer,
  FilterFieldContent,
  FilterFieldLabel,
} from "../shared";

const NONE = "__none__";

function gradeOptions(noneLabel: string) {
  return [
    { value: NONE, label: noneLabel },
    ...GS_GRADES.map((g) => ({ value: String(g), label: gsGradeLabel(g) })),
  ];
}

const MIN_GRADE_OPTIONS = gradeOptions("No Min");
const MAX_GRADE_OPTIONS = gradeOptions("No Max");

export function FilterFederalSection() {
  const { filters, updateFilter } = useTalentSearchFilters();
  const series = filters.occupational_series ?? [];
  const gsMin = filters.gs_grade_min ?? undefined;
  const gsMax = filters.gs_grade_max ?? undefined;

  const setGsMin = (raw: string) => {
    const next = raw === NONE ? undefined : Number(raw);
    if (next != null && gsMax != null && next > gsMax) updateFilter("gs_grade_max", next);
    updateFilter("gs_grade_min", next);
  };
  const setGsMax = (raw: string) => {
    const next = raw === NONE ? undefined : Number(raw);
    if (next != null && gsMin != null && next < gsMin) updateFilter("gs_grade_min", next);
    updateFilter("gs_grade_max", next);
  };

  const addSeries = (text: string) => {
    const code = seriesCode(text) ?? normalizeSeriesFreeText(text);
    if (!code || series.length >= MAX_OCCUPATIONAL_SERIES) return;
    if (series.some((s) => seriesCode(s.text) === code)) return;
    updateFilter("occupational_series", [...series, { text, required: false }]);
  };

  return (
    <>
      <FilterFieldContainer>
        <FilterFieldLabel>Occupational Series</FilterFieldLabel>
        <FilterFieldContent>
          <AutocompleteInput
            variant="filter"
            field="occupational_series"
            placeholder="Code or title, e.g. 2210"
            onSelect={(result: AutocompleteResult) => addSeries(result.value)}
            onFreeText={(text: string) => {
              const code = normalizeSeriesFreeText(text);
              if (code) addSeries(code);
            }}
          />
        </FilterFieldContent>
      </FilterFieldContainer>
      {series.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {series.map((s, i) => {
            const excluded = s.mode === "exclude";
            const required = s.required ?? false;
            return (
              <FilterChip
                key={`${s.text}-${i}`}
                label={s.text ?? ""}
                excluded={excluded}
                required={required}
                onToggleExclude={() => {
                  const next = [...series];
                  next[i] = { ...s, mode: excluded ? "include" : "exclude" };
                  updateFilter("occupational_series", next);
                }}
                onToggleRequired={() => {
                  const next = [...series];
                  next[i] = { ...s, required: !required };
                  updateFilter("occupational_series", next);
                }}
                onRemove={() =>
                  updateFilter(
                    "occupational_series",
                    series.filter((_, j) => j !== i),
                  )
                }
              />
            );
          })}
          <ClearChipsButton onClick={() => updateFilter("occupational_series", undefined)} />
        </div>
      )}
      <FilterFieldContainer>
        <FilterFieldLabel>GS Grade</FilterFieldLabel>
        <FilterFieldContent>
          <div className="flex w-full min-w-0 items-center">
            <span className="mr-1.5 shrink-0 whitespace-nowrap text-sm font-normal text-contrast/80">Between</span>
            <div className="min-w-0 flex-1" aria-label="Minimum GS Grade">
              <FilterSelectInput
                options={MIN_GRADE_OPTIONS}
                value={gsMin != null ? String(gsMin) : NONE}
                onChange={setGsMin}
                placeholder="No Min"
              />
            </div>
            <span className="mx-1.5 shrink-0 whitespace-nowrap text-sm font-normal text-contrast/80">and</span>
            <div className="min-w-0 flex-1" aria-label="Maximum GS Grade">
              <FilterSelectInput
                options={MAX_GRADE_OPTIONS}
                value={gsMax != null ? String(gsMax) : NONE}
                onChange={setGsMax}
                placeholder="No Max"
              />
            </div>
          </div>
        </FilterFieldContent>
      </FilterFieldContainer>
      <FilterCheckboxInput
        checked={filters.gs_include_equivalents ?? false}
        onChange={(v) => updateFilter("gs_include_equivalents", v || undefined)}
      >
        Include equivalent experience (draft)
      </FilterCheckboxInput>
    </>
  );
}
